import { Link } from 'react-router'
import { AngledBand } from '@carouselgg/nexus/angled-band'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { ScrollReveal } from '@carouselgg/nexus/scroll-reveal'
import { ArrowRight } from '@carouselgg/nexus/icon'
import { useTrackVisibility } from '../hooks/useTrackVisibility'
import styles from './HowItWorks.module.css'

const STEPS = [
  {
    seed: '01',
    title: 'Claim your identity',
    body: 'Link your game accounts and verify a phone number. One phone, one account — that is where smurfing starts to die.',
  },
  {
    seed: '02',
    title: 'Build a Trust Score',
    body: 'Your ranked history, account age and linked platforms roll into one number leagues can act on — visible to you, always explained.',
  },
  {
    seed: '03',
    title: 'Join or start a league',
    body: 'Find a season at your Elo, or open your own with registration, divisions and a public page in an afternoon.',
  },
  {
    seed: '04',
    title: 'Play the split',
    body: 'Schedules, results and standings update themselves. Playoffs seed from the table. Your record follows you to the next league.',
  },
]

/** Bracket elbows connecting the four steps, in the shape of an 8-team draw. */
function ConnectorRail() {
  return (
    <svg className={styles.railSvg} viewBox="0 0 1344 22" preserveAspectRatio="none" aria-hidden="true">
      <g stroke="var(--nexus-border)" strokeWidth="1.4" fill="none">
        <path d="M168 22 V11 H504 V22" />
        <path d="M840 22 V11 H1176 V22" />
        <path d="M336 11 V4 H1008 V11" />
      </g>
      <circle cx="168" cy="22" r="3" fill="var(--nexus-purple)" />
      <circle cx="504" cy="22" r="3" fill="var(--nexus-pink)" />
      <circle cx="840" cy="22" r="3" fill="var(--nexus-blue)" />
      <circle cx="1176" cy="22" r="3" fill="var(--nexus-green)" />
    </svg>
  )
}

export function HowItWorks() {
  const ref = useTrackVisibility<HTMLDivElement>('how-it-works')

  return (
    <AngledBand id="how-it-works" tone="base" slice="top" reverse>
      <div className={styles.inner} ref={ref}>
        <ScrollReveal>
          <div className={styles.head}>
            <div>
              <p className={styles.eyebrow}>
                <span className={styles.seed}>06</span>
                <span className={styles.rule} />
                How it works
              </p>
              <DisplayHeading size="6xl">Four steps to a real season.</DisplayHeading>
            </div>
            <Link to="/how-it-works" className={styles.headLink}>
              Read the full walkthrough
              <ArrowRight size={15} />
            </Link>
          </div>
        </ScrollReveal>

        <ConnectorRail />

        <div className={styles.steps}>
          {STEPS.map((step, index) => (
            <ScrollReveal key={step.seed} delay={index * 70}>
              <div className={styles.step}>
                <span className={styles.stepSeed}>{step.seed}</span>
                <DisplayHeading as="h3" size="3xl" cut="soft">
                  {step.title}
                </DisplayHeading>
                <p className={styles.stepBody}>{step.body}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </AngledBand>
  )
}
